// Centralized legal content + provider configuration for INDEPENDENT.
// Update provider identifiers and email operability here when the domain is activated.

export const TERMS_VERSION = "1.0";
export const LEGAL_LAST_UPDATED = "15 de septiembre de 2026";

// Operational contact email for the domain.
export const LEGAL_CONTACT_EMAIL = "soporte@independent-audit.es";
export const SUPPORT_CONTACT_EMAIL = "soporte@independent-audit.es";

// Provider / fiscal configuration — real, definitive data of the service provider.
export const PROVIDER = {
  name: "Marcos Moraza Millán",
  role: "Profesional autónomo (persona física)",
  country: "España",
  nif: "73024980A",
  address: "Calle Juan de Lanuza 9, 50700 Caspe, Zaragoza, España",
  brand: "INDEPENDENT",
};

// Official service domain. DNS is being configured externally.
export const SITE = {
  domain: "independent-audit.es",
  domainActive: false,
  legalContactEmail: LEGAL_CONTACT_EMAIL,
  supportContactEmail: SUPPORT_CONTACT_EMAIL,
  emailsOperational: true,
};

export const CONTACT_NOTE =
  `Para cualquier consulta legal, de privacidad o de soporte, puedes escribir a ${LEGAL_CONTACT_EMAIL} ` +
  "o dirigirte por escrito al domicilio del titular indicado en el Aviso Legal.";

const RENEWAL_CANCELLATION = {
  h: "Renovación automática y cancelación",
  p: [
    "Las suscripciones de pago se renuevan automáticamente al final de cada ciclo de facturación hasta que se cancelen.",
    "Puedes cancelar en cualquier momento desde el mecanismo de gestión de suscripción disponible en la aplicación (portal de facturación).",
  ],
  list: [
    "La cancelación detiene las renovaciones futuras.",
    "La cancelación no finaliza de forma inmediata el periodo de facturación ya pagado: conservas el acceso a la suscripción hasta el final de dicho periodo.",
    "Los créditos de plan mensuales no utilizados caducan según las reglas de su ciclo de facturación (no se acumulan).",
    "Los créditos comprados (top-ups) se conservan tras la cancelación.",
  ],
};

export const DOCS = {
  terms: {
    key: "terms",
    title: "Términos y condiciones del servicio",
    intro:
      "Estos Términos regulan el acceso y uso de INDEPENDENT, un servicio de software como servicio (SaaS). Al crear una cuenta o utilizar el servicio, aceptas estos Términos.",
    sections: [
      {
        h: "1. Descripción del servicio",
        p: [
          "INDEPENDENT es una plataforma SaaS que ayuda a propietarios de empresas y a sus equipos a: diagnosticar la dependencia operativa; identificar dependencias del propietario y de personas clave; priorizarlas; crear intervenciones y sistemas operativos; guiar su implementación; recopilar evidencia operativa; y evaluar si la dependencia se ha reducido.",
          "El asistente «Architect» utiliza inteligencia artificial como parte del servicio. Los resultados de INDEPENDENT constituyen apoyo a la decisión y orientación operativa.",
        ],
      },
      {
        h: "2. Ausencia de garantías de resultado",
        p: [
          "INDEPENDENT no garantiza la independencia del negocio, la rentabilidad, el rendimiento financiero, el cumplimiento normativo, resultados laborales, el éxito operativo ni ningún resultado específico.",
          "El cliente sigue siendo el único responsable de sus decisiones de negocio y de su implementación.",
        ],
      },
      {
        h: "3. Inteligencia artificial",
        p: [
          "Partes de INDEPENDENT utilizan inteligencia artificial. Las recomendaciones generadas por IA pueden contener errores o requerir juicio humano.",
        ],
        list: [
          "Debes revisar las recomendaciones antes de implementar decisiones de negocio materiales.",
          "INDEPENDENT no sustituye el asesoramiento profesional regulado (legal, fiscal, contable, laboral, financiero u otro).",
          "Los cambios estructurales que la aplicación somete a confirmación permanecen sujetos a tu decisión explícita.",
        ],
      },
      {
        h: "4. Cuenta y uso aceptable",
        p: ["Para usar el servicio debes crear una cuenta y facilitar información veraz y actualizada. Eres responsable de la seguridad de tus credenciales y de la actividad realizada bajo tu cuenta."],
        list: [
          "Uso únicamente para fines autorizados y lícitos.",
          "Prohibido el abuso, la interferencia con el servicio o su infraestructura y el acceso no autorizado.",
          "Prohibido el uso indebido de la IA o de la infraestructura del servicio.",
          "INDEPENDENT podrá suspender o cancelar el acceso de forma razonable ante abusos o por motivos de seguridad.",
        ],
      },
      {
        h: "5. Modelo comercial",
        p: ["El modelo comercial vigente es el siguiente:"],
        list: [
          "Free — 0€: 25 créditos de bienvenida (una sola vez).",
          "Starter — 19,99€/mes: 30 créditos de plan por ciclo de facturación.",
          "Pro — 39,99€/mes: 75 créditos de plan por ciclo de facturación.",
          "Scale — 79,99€/mes: 180 créditos de plan por ciclo de facturación.",
          "Operaciones de pago: Architect Build = 15 créditos; Rediseño sustancial de sistema = 5 créditos.",
          "Top-ups: 15 créditos = 7,99€; 50 créditos = 19,99€; 150 créditos = 49,99€.",
        ],
        after: [
          "Los precios pueden modificarse con efectos hacia el futuro, con el preaviso adecuado y conforme a la ley aplicable. No se alterará un periodo de facturación ya pagado.",
        ],
      },
      {
        h: "6. Reglas de créditos",
        subsections: [
          {
            h: "Créditos de plan",
            list: [
              "Se incluyen con los planes de suscripción de pago.",
              "Se asignan/reinician en cada ciclo de facturación.",
              "Los créditos de plan no utilizados no se acumulan al ciclo siguiente.",
              "No tienen valor en efectivo ni son canjeables por dinero.",
              "No son transferibles entre cuentas no relacionadas, salvo que INDEPENDENT lo habilite expresamente en el futuro.",
            ],
          },
          {
            h: "Créditos comprados (top-ups)",
            list: [
              "Se adquieren por separado.",
              "No se destruyen por la renovación de la suscripción.",
              "No se destruyen por la cancelación.",
              "No se destruyen por un cambio a un plan inferior (downgrade).",
              "Permanecen asociados a la cuenta del cliente conforme a las condiciones del servicio.",
              "Los créditos consumidos no se reutilizan ni se reconvierten en efectivo, salvo que la ley imperativa lo exija.",
            ],
          },
          {
            h: "Prioridad de consumo",
            list: ["Los créditos de plan se consumen antes que los créditos comprados."],
          },
          {
            h: "Créditos de bienvenida / promocionales",
            list: [
              "Los créditos promocionales (incluidos los 25 créditos de bienvenida) se distinguen de los créditos comprados.",
              "INDEPENDENT puede establecer condiciones razonables para los créditos promocionales.",
              "Los créditos promocionales no gozan necesariamente del mismo tratamiento que los créditos comprados.",
            ],
          },
        ],
      },
      RENEWAL_CANCELLATION,
      {
        h: "8. Reembolsos",
        p: ["Esta política se aplica siempre sin perjuicio de los derechos imperativos que la legislación de consumo te reconozca."],
        list: [
          "Los pagos de suscripción correspondientes a un periodo de facturación ya iniciado son, con carácter general, no reembolsables.",
          "Podrán aplicarse excepciones: derechos legales imperativos, cargos duplicados, errores de facturación u otras circunstancias en las que INDEPENDENT esté legalmente obligado a reembolsar.",
          "Los créditos comprados ya consumidos no son reembolsables, salvo que la ley imperativa exija otra cosa.",
        ],
        after: [
          "Derecho de desistimiento: en la contratación de servicios y contenidos digitales, si solicitas el inicio inmediato de la prestación y esta se ejecuta durante el plazo de desistimiento, podrías perder o ver limitado dicho derecho en los términos previstos por la normativa de consumo aplicable. Cuando el desistimiento sea aplicable, se informará del procedimiento para ejercerlo.",
        ],
      },
      {
        h: "9. Disponibilidad internacional e impuestos",
        p: [
          "El servicio puede contratarse internacionalmente. El mercado comercial inicial principal es España / la Unión Europea.",
          "Los impuestos se aplican conforme a la ley aplicable y a la configuración de facturación vigente en cada momento. INDEPENDENT no declara estar registrado fiscalmente en jurisdicciones donde ello no haya sido confirmado.",
        ],
      },
      {
        h: "10. Procesamiento de pagos",
        p: [
          "Los pagos se procesan a través de Stripe. La información de pago puede ser tratada por el proveedor de pagos conforme a sus propios términos y marco de privacidad.",
          "Las suscripciones y los top-ups se facturan a través de la infraestructura de Stripe implementada. INDEPENDENT no almacena los datos completos de tu tarjeta.",
        ],
      },
      {
        h: "11. Propiedad intelectual",
        p: ["El software, la marca y los contenidos de INDEPENDENT están protegidos por los derechos de propiedad intelectual e industrial aplicables. Conservas la titularidad de la información de negocio que introduces en el servicio."],
      },
      {
        h: "12. Limitación de responsabilidad",
        p: ["En la máxima medida permitida por la ley, INDEPENDENT no será responsable de daños indirectos, incidentales o consecuenciales derivados del uso del servicio. Ninguna disposición de estos Términos limita la responsabilidad que no pueda excluirse conforme a la ley imperativa aplicable."],
      },
      {
        h: "13. Modificaciones de los Términos",
        p: ["Podemos actualizar estos Términos con efectos hacia el futuro. Se indicará la fecha de última actualización y, cuando proceda, se informará de los cambios sustanciales."],
      },
      {
        h: "14. Ley aplicable",
        p: ["Estos Términos se rigen por la legislación española y de la Unión Europea, sin perjuicio de las normas imperativas de protección del consumidor del país de residencia del usuario."],
      },
    ],
  },

  privacy: {
    key: "privacy",
    title: "Política de privacidad",
    intro:
      "Esta Política explica cómo INDEPENDENT trata los datos personales, de acuerdo con el Reglamento (UE) 2016/679 (RGPD) y la normativa española aplicable.",
    sections: [
      {
        h: "1. Responsable del tratamiento",
        p: [`${PROVIDER.name} — ${PROVIDER.role} (${PROVIDER.country}). NIF: ${PROVIDER.nif}. Domicilio: ${PROVIDER.address}. Contacto en materia de privacidad: ${LEGAL_CONTACT_EMAIL}.`],
      },
      {
        h: "2. Categorías de datos personales",
        list: [
          "Datos de cuenta: nombre, correo electrónico y credenciales (almacenadas de forma cifrada mediante hash).",
          "Datos de facturación y cliente: plan, historial de créditos y referencias de pago (los datos de pago los trata Stripe).",
          "Información de negocio que introduces en INDEPENDENT: dependencias, procesos, decisiones, sistemas y evidencia operativa.",
          "Datos de uso y técnicos: registros de actividad e identificadores de sesión, cuando se recogen para prestar y asegurar el servicio.",
          "Datos de analítica de producto (solo con tu consentimiento): eventos de uso, un identificador anónimo/de usuario, datos de atribución de la visita (parámetros UTM, referrer, página de aterrizaje) y grabaciones de sesión con los campos de entrada enmascarados. Tratados mediante PostHog (UE).",
          "Origen del alta: al registrarte guardamos en tu cuenta, como dato propio y sin cookies de terceros, la campaña o enlace por el que llegaste (parámetros UTM, fbclid/gclid, referrer y página de entrada), conservados solo durante la pestaña del navegador (sessionStorage) hasta el registro. Base jurídica: interés legítimo en medir la eficacia de nuestras campañas.",
          "Comunicaciones de soporte que nos envías.",
        ],
      },
      {
        h: "3. Finalidades del tratamiento",
        list: [
          "Prestar y mantener el servicio (diagnóstico, Architect, sistemas y verificación).",
          "Gestionar cuentas, suscripciones, créditos y pagos.",
          "Garantizar la seguridad y prevenir el abuso.",
          "Atender solicitudes de soporte.",
          "Analítica de producto y mejora de la experiencia (Session Replay), únicamente sobre la base de tu consentimiento.",
          "Cumplir obligaciones legales.",
        ],
      },
      {
        h: "4. Bases jurídicas",
        list: [
          "Ejecución del contrato (prestación del servicio y facturación).",
          "Interés legítimo (seguridad, prevención del abuso y mejora del servicio).",
          "Cumplimiento de obligaciones legales (por ejemplo, contables/fiscales).",
          "Consentimiento, cuando resulte aplicable.",
        ],
      },
      {
        h: "5. Encargados y proveedores",
        p: ["Recurrimos a los siguientes proveedores para prestar el servicio (verificados en la configuración actual de la aplicación):"],
        list: [
          "Stripe — procesamiento de pagos.",
          "Emergent — plataforma de alojamiento/infraestructura y pasarela de servicios de IA, así como inicio de sesión con Google gestionado.",
          "Anthropic (modelo Claude) — generación de texto de IA, a través de la pasarela de Emergent.",
          "OpenAI (Whisper) — transcripción de voz a texto, a través de la pasarela de Emergent.",
          "Google — inicio de sesión con Google (OAuth), cuando el usuario lo utiliza.",
          "PostHog (instancia en la Unión Europea) — analítica de producto y Session Replay, únicamente si el usuario presta su consentimiento.",
          "FirstPromoter — atribución del programa de afiliados (reconocimiento de recomendaciones para el pago de comisiones). La contabilidad de comisiones y los pagos los gestiona FirstPromoter conectado a Stripe.",
          "Base de datos MongoDB alojada en la infraestructura de la plataforma.",
        ],
        after: [
          "En revisión legal: las condiciones específicas de retención y de uso para entrenamiento por parte de los proveedores de IA, así como la identidad definitiva de los subencargados de alojamiento, deben confirmarse antes del lanzamiento.",
        ],
      },
      {
        h: "6. Datos de negocio y procesamiento por IA",
        p: [
          "Distinguimos entre: (a) información de cuenta/personal; (b) información operativa/de negocio que introduces; (c) entradas y salidas de IA; y (d) información de facturación.",
          "La información de negocio y las entradas de IA se procesan para generar diagnósticos, recomendaciones y verificaciones. No afirmamos que estos datos nunca se utilicen para entrenamiento, que nunca se conserven, que se almacenen exclusivamente en Europa, ni que estén sujetos a un cifrado o certificación concretos, salvo en la medida en que ello se confirme técnicamente. Estas cuestiones están señaladas para revisión legal.",
        ],
      },
      {
        h: "7. Transferencias internacionales",
        p: ["Algunos proveedores pueden tratar datos fuera del Espacio Económico Europeo. Cuando existan tales transferencias, se ampararán en las garantías previstas por el RGPD (por ejemplo, cláusulas contractuales tipo). La confirmación de cada transferencia está señalada para revisión legal."],
      },
      {
        h: "8. Conservación",
        p: ["Conservamos los datos mientras la cuenta esté activa y durante los plazos necesarios para cumplir obligaciones legales o resolver disputas. Tras ello, se suprimen o anonimizan."],
      },
      {
        h: "9. Seguridad",
        p: ["Aplicamos medidas técnicas y organizativas proporcionadas al riesgo, incluyendo el almacenamiento de contraseñas mediante hash y el uso de cookies de sesión seguras."],
      },
      {
        h: "10. Tus derechos (RGPD)",
        list: [
          "Acceso, rectificación y supresión.",
          "Limitación del tratamiento.",
          "Portabilidad de los datos.",
          "Oposición al tratamiento.",
          "Presentar una reclamación ante la autoridad de control competente (en España, la Agencia Española de Protección de Datos, www.aepd.es).",
        ],
        after: [`Para ejercer tus derechos, contacta en ${LEGAL_CONTACT_EMAIL}. También puedes dirigirte por escrito al domicilio del titular indicado en el Aviso Legal.`],
      },
    ],
  },

  cookies: {
    key: "cookies",
    title: "Política de cookies",
    intro:
      "Esta Política describe las cookies y tecnologías similares que utiliza actualmente INDEPENDENT. Basado en una auditoría del frontend y del backend actuales.",
    sections: [
      {
        h: "1. Qué son las cookies",
        p: ["Las cookies son pequeños archivos que un sitio almacena en tu navegador para funcionar correctamente y recordar tu sesión."],
      },
      {
        h: "2. Cookies estrictamente necesarias",
        p: ["INDEPENDENT utiliza cookies estrictamente necesarias para la autenticación y la seguridad. Estas no requieren consentimiento."],
        list: [
          "access_token — cookie de sesión de autenticación (JWT), httpOnly y segura. Necesaria para mantener tu sesión iniciada.",
          "session_token — cookie de sesión para el inicio de sesión con Google (cuando se utiliza), httpOnly y segura.",
          "__cf_bm — cookie técnica de seguridad establecida por Cloudflare (gestión de bots/proxy).",
        ],
      },
      {
        h: "3. Cookies y analítica sujetas a consentimiento",
        p: [
          "Utilizamos analítica de producto propia mediante PostHog (instancia alojada en la Unión Europea) para medir el uso, mejorar el producto y detectar problemas de experiencia (incluida la grabación de sesión / Session Replay).",
          "Esta analítica es NO esencial y solo se activa si prestas tu consentimiento a través del aviso de cookies. Hasta que aceptas, PostHog no captura eventos, no graba sesiones y no almacena cookies ni identificadores en tu navegador. Si rechazas, permanece completamente desactivada.",
        ],
        list: [
          "Almacenamiento de PostHog (cookies y/o almacenamiento local) para conservar un identificador anónimo/de usuario y la atribución de la visita (por ejemplo, parámetros UTM y referrer). Se establece únicamente tras tu consentimiento.",
          "Session Replay con privacidad conservadora: todos los campos de entrada se enmascaran y no se capturan contraseñas, datos de pago, cabeceras de autorización ni tokens.",
          "Atribución de afiliados (FirstPromoter): cookies _fprom_ref y _fprom_tid que registran, cuando llegas a través del enlace de un afiliado, qué recomendación originó tu visita, para reconocer la comisión correspondiente. Son cookies de atribución, no contienen datos de pago y puedes eliminarlas desde tu navegador.",
          "Origen de la visita (ind_attribution, sessionStorage): guarda los parámetros de campaña de tu llegada solo mientras la pestaña está abierta, para asociarlos a tu cuenta si te registras. No se comparte con terceros y se borra al cerrar la pestaña.",
        ],
        after: [
          "Puedes retirar tu consentimiento en cualquier momento borrando las cookies/almacenamiento del sitio desde tu navegador; entonces la analítica dejará de estar activa.",
        ],
      },
      {
        h: "4. Cookies de marketing/publicidad",
        p: ["No utilizamos cookies de marketing ni de publicidad."],
      },
      {
        h: "5. Gestión de cookies",
        p: ["Puedes bloquear o eliminar cookies desde la configuración de tu navegador. Ten en cuenta que desactivar las cookies necesarias impedirá iniciar sesión y usar el servicio."],
      },
    ],
  },

  legal: {
    key: "legal",
    title: "Aviso legal",
    intro:
      "Información de identificación del prestador del servicio, conforme a la Ley 34/2002 (LSSI-CE) y demás normativa aplicable.",
    sections: [
      {
        h: "1. Titular del servicio",
        list: [
          `Titular: ${PROVIDER.name}`,
          `Condición: ${PROVIDER.role}`,
          `País: ${PROVIDER.country}`,
          `NIF/DNI: ${PROVIDER.nif}`,
          `Domicilio a efectos de notificaciones: ${PROVIDER.address}`,
          `Marca comercial: ${PROVIDER.brand}`,
          `Dominio oficial: ${SITE.domain}`,
          `Contacto: ${LEGAL_CONTACT_EMAIL}`,
        ],
      },
      {
        h: "2. Objeto",
        p: ["INDEPENDENT es un servicio de software como servicio (SaaS) dirigido a la reducción de la dependencia operativa de empresas. El acceso y uso del servicio se rige por los Términos y condiciones y por la Política de privacidad."],
      },
      {
        h: "3. Canales de contacto",
        p: ["La identificación fiscal del titular (nombre, NIF y domicilio) es definitiva. El canal de contacto para cuestiones legales, de privacidad y de soporte es soporte@independent-audit.es."],
      },
    ],
  },
};
